import { Box, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { colors } from '../../components';
import {
    AutoPayBadgeStyles,
    AutopayInfoWrapper,
    LinearProgressWrapper,
} from '../../components/global-styles/GlobalStyles';
import CommonIconAvatar from '../../components/ui-components/CommonIconAvatar';
import CommonStatusChip from '../../components/ui-components/StatusChipComponent';
import { RootState } from '../../store';
import { trackContractSelected } from '../../utils/google-analytics';
import { formatCurrency } from '../../utils/helper';
import { NavigationConfig } from '../../utils/navigation-constants';
import { useTranslation } from '../../utils/translations';

import { ContractPaymentInfoRow, NoContractsFound } from './contractListShared';

export interface ContractListItem {
    id: string;
    contractId?: string;
    assetDesc?: string;
    lastPayment?: string;
    currentPaymentDue?: string;
    currentPaymentDueDate?: string;
    isCurrentDueActual?: boolean;
    isPastDueActual?: boolean;
    nextPayment?: string;
    amountLabel?: string;
    amount?: string;
    apiContract?: Partial<import('../../services/contract-services').ContractApiResponse> | null;
}

interface ActiveListProps {
    contractList: ContractListItem[];
    isFetching?: boolean;
}

const ActiveList: React.FC<ActiveListProps> = ({ contractList, isFetching = false }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isLoading = useSelector((state: RootState) => state.app.activeHTTPRequests > 0);
    const handleToProceed = (contract: ContractListItem) => {
        // TODO: Keep read-only contract detail navigation available during impersonation.
        // Pass the full API contract data directly from the stored data
        if (contract.apiContract) {
            trackContractSelected(
                contract.apiContract.AspireId || String(contract.apiContract.Id ?? ''),
                'active_list',
            );
            navigate(NavigationConfig.ContractDetails, { state: { contract: contract.apiContract } });
        }
    };

    return (
        <Stack>
            <Grid container spacing={2}>
                {contractList?.length > 0
                    ? contractList?.map(contract => {
                          // Get data from original API contract
                          const apiContract = contract.apiContract;
                          const paymentsReceived = apiContract?.PaymentsReceived ?? 0;
                          const numberOfPayments = apiContract?.NumberOfPayments ?? 1;
                          const paymentPercentage =
                              paymentsReceived > 0 ? (paymentsReceived / numberOfPayments) * 100 : 0;
                          const pastDueAmount = Number(apiContract?.PastDue) || 0;
                          return (
                              <Grid
                                  size={{ xs: 12, lg: 6 }}
                                  key={contract.id}
                                  tabIndex={0}
                                  role="button"
                                  aria-label={`Open contract ${contract.contractId}`}
                                  onClick={() => handleToProceed(contract)}
                                  onKeyDown={e => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          handleToProceed(contract);
                                      }
                                  }}
                                  sx={{
                                      cursor: 'pointer',
                                      pointerEvents: 'auto',
                                  }}
                              >
                                  <Stack
                                      sx={{
                                          borderRadius: 3,
                                          position: 'relative',
                                          overflow: 'hidden',
                                          p: 2,
                                          gap: 2.5,
                                          border: `1px solid ${colors.greyPreset[30]}`,
                                          '&:focus-visible': {
                                              outline: `2px solid ${colors.fullBlack}`,
                                              outlineOffset: '4px',
                                              borderRadius: 3,
                                          },
                                      }}
                                  >
                                      {/* Header Row */}
                                      <Box
                                          display="flex"
                                          justifyContent="space-between"
                                          alignItems="flex-start"
                                          gap={1}
                                      >
                                          <Box display="flex" alignItems="start" gap={1.5} sx={{ minWidth: 0 }}>
                                              <Stack direction="row" alignItems="center" gap={1.5}>
                                                  <CommonIconAvatar
                                                      icon="icon-contracts"
                                                      iconBgColor={colors.primaryLight[30]}
                                                      iconColor={colors.primaryLight[100]}
                                                  />
                                                  <Box>
                                                      <Stack
                                                          direction={'row'}
                                                          gap={1.5}
                                                          alignItems={'center'}
                                                          flexWrap="wrap"
                                                      >
                                                          <Typography
                                                              variant="subtitle1"
                                                              component="h2"
                                                              fontWeight={600}
                                                          >
                                                              {contract.contractId}
                                                          </Typography>
                                                          <CommonStatusChip
                                                              label={
                                                                  contract?.apiContract?.IsLoan
                                                                      ? t('contracts.details.Loan')
                                                                      : t('contracts.details.Lease')
                                                              }
                                                              chipBgColor={colors.bluePreset[30]}
                                                          />
                                                          {apiContract?.AutoPay && (
                                                              <AutoPayBadgeStyles direction="row" alignItems="center">
                                                                  <span className="icon-check" />
                                                                  <Typography variant="subtitle2" component="h3">
                                                                      {t('common.AutoPay')}
                                                                  </Typography>
                                                              </AutoPayBadgeStyles>
                                                          )}
                                                          {Number(apiContract?.PastDue) > 0 && (
                                                              <CommonStatusChip
                                                                  label="Action Required"
                                                                  chipBgColor={colors.errorPreset[20]}
                                                                  chipBorderColor={colors.errorPreset[30]}
                                                                  chipTextColor={colors.errorPreset[10]}
                                                                  sx={{ display: { xs: 'none', lg: 'inline-block' } }}
                                                              />
                                                          )}
                                                      </Stack>
                                                      <Typography
                                                          variant="caption"
                                                          color="textSecondary"
                                                          component="h2"
                                                      >
                                                          {contract.assetDesc ?? ''}
                                                      </Typography>
                                                  </Box>
                                              </Stack>
                                          </Box>
                                          {Number(apiContract?.PastDue) > 0 && (
                                              <CommonStatusChip
                                                  label="Action Required"
                                                  chipBgColor={colors.errorPreset[20]}
                                                  chipBorderColor={colors.errorPreset[30]}
                                                  chipTextColor={colors.errorPreset[10]}
                                                  sx={{ display: { xs: 'inline-block', lg: 'none' }, flexShrink: 0 }}
                                              />
                                          )}
                                      </Box>

                                      <AutopayInfoWrapper direction="row" alignItems="center" mb={1}>
                                          <span className="icon-info-circle" />
                                          <Typography variant="body2">
                                              {t('contracts.details.clickForDetails') ||
                                                  'Click the contract to view additional details.'}
                                          </Typography>
                                      </AutopayInfoWrapper>

                                      {/* Status + Progress Bar */}
                                      <Box>
                                          <Typography
                                              id={`payment-progress-label-${contract.id}`}
                                              variant="caption"
                                              color="textSecondary"
                                              display="flex"
                                              justifyContent="space-between"
                                              pb={1}
                                              alignItems="center"
                                          >
                                              {t('contracts.details.paymentProgress')}
                                              <Typography component={'span'} variant="caption" color="textPrimary">
                                                  {apiContract?.PaymentsReceived} {t('contracts.list.of')}{' '}
                                                  {apiContract?.NumberOfPayments} {t('contracts.list.paymentsMade')}
                                              </Typography>
                                          </Typography>
                                          <LinearProgressWrapper
                                              variant="determinate"
                                              value={paymentPercentage}
                                              aria-labelledby={`payment-progress-label-${contract.id}`}
                                          />
                                      </Box>
                                      {/* Payment Info */}
                                      <Box
                                          display="grid"
                                          sx={{
                                              gridTemplateColumns: 'repeat(2, 1fr)',
                                              columnGap: { xs: 2, sm: 4 },
                                              rowGap: 2,
                                          }}
                                      >
                                          <ContractPaymentInfoRow
                                              currentPaymentDueDate={contract.currentPaymentDueDate}
                                              currentPaymentDue={
                                                  pastDueAmount > 0
                                                      ? formatCurrency(pastDueAmount)
                                                      : contract.currentPaymentDue
                                              }
                                              isCurrentDueActual={
                                                  pastDueAmount > 0 ? true : contract.isCurrentDueActual
                                              }
                                              isPastDueActual={contract.isPastDueActual}
                                              nextPayment={contract.nextPayment}
                                              amountLabel={contract.amountLabel}
                                              amount={contract.amount}
                                              lastPayment={contract.lastPayment}
                                          />
                                      </Box>
                                  </Stack>
                              </Grid>
                          );
                      })
                    : !isLoading && !isFetching && <NoContractsFound message={t('contracts.list.noActiveContractsFound')} />}
            </Grid>
        </Stack>
    );
};

export default ActiveList;
